import { getSession, loadEnv } from '@/core';
import { httpClient } from './httpClient';
import { parseChecklist, parseEquipmentCategories, parsePaymentMethods, parseStepSuggestions } from './schemas/service';
import { parseMaintenanceDetail, parseMaintenanceList } from './schemas/technician';
import type { CategoryOption, ChecklistItem, PaymentMethodOption } from '@/domain/service';
import type { MaintenanceDetail, MaintenanceItem, MaintenancePhotoUploadType } from '@/domain/technician';

export type ServiceGroup = 'in_progress' | 'scheduled' | 'completed';

function technicianId(): string {
  const user = getSession().user;
  if (user === null) throw new Error('Sessão sem técnico vinculado.');
  return user.id;
}

/** GET /technicians/{id}/maintenance-requests?group=… — lista agrupada (TEL-04). */
export async function fetchServices(group: ServiceGroup, page = 1, size = 20): Promise<{ items: MaintenanceItem[]; totalCount: number }> {
  const payload = await httpClient.get<unknown>(
    `/technicians/${technicianId()}/maintenance-requests?group=${group}&page=${String(page)}&size=${String(size)}`,
  );
  return parseMaintenanceList(payload);
}

/** GET /maintenance-requests/{id} — detalhe do chamado em atendimento (TEL-05). */
export async function fetchServiceDetail(id: string): Promise<MaintenanceDetail> {
  const payload = await httpClient.get<unknown>(`/maintenance-requests/${id}`);
  return parseMaintenanceDetail(payload);
}

/** POST /maintenance-requests/{id}/en-route — "Estou a caminho" (ACEITA → A_CAMINHO). */
export async function enRoute(id: string): Promise<void> {
  await httpClient.post<unknown>(`/maintenance-requests/${id}/en-route`);
}

/** POST /maintenance-requests/{id}/arrived — "Cheguei na clínica" (A_CAMINHO → NO_LOCAL). */
export async function arrived(id: string): Promise<void> {
  await httpClient.post<unknown>(`/maintenance-requests/${id}/arrived`);
}

/** POST /maintenance-requests/{id}/start — "Iniciar atendimento" (NO_LOCAL → EM_ANDAMENTO). */
export async function startService(id: string): Promise<void> {
  await httpClient.post<unknown>(`/maintenance-requests/${id}/start`);
}

/** POST /maintenance-requests/{id}/complete — conclusão do atendimento (EM_ANDAMENTO). */
export async function completeService(id: string, notes: string | null): Promise<void> {
  await httpClient.post<unknown>(`/maintenance-requests/${id}/complete`, { finalValue: null, paymentMethodId: null, technicianNotes: notes });
}

/** POST /maintenance-requests/{id}/give-up — desistência com justificativa (devolve a cota). */
export async function giveUp(id: string, reason: string): Promise<void> {
  await httpClient.post<unknown>(`/maintenance-requests/${id}/give-up`, { reason });
}

/** GET /maintenance-requests/{id}/checklist — itens do checklist (TEL-05). */
export async function fetchChecklist(id: string): Promise<ChecklistItem[]> {
  const payload = await httpClient.get<unknown>(`/maintenance-requests/${id}/checklist`);
  return parseChecklist(payload);
}

/** PUT /maintenance-requests/checklist/{itemId} — toggle de item feito. */
export async function updateChecklistItem(itemId: string, isDone: boolean): Promise<void> {
  await httpClient.put<unknown>(`/maintenance-requests/checklist/${itemId}`, { isDone });
}

/**
 * Busca autenticada do conteúdo de uma foto (proxy exige Authorization — `<img src>` direto não
 * funciona). Devolve object URL para preview; o chamador revoga quando descartar.
 */
export async function fetchPhotoPreview(contentUrl: string): Promise<string> {
  const env = loadEnv();
  const token = getSession().accessToken;
  const response = await fetch(`${env.VITE_API_BASE_URL}${contentUrl}`, {
    credentials: 'include',
    headers: {
      Accept: 'image/*',
      ...(token !== null ? { Authorization: `Bearer ${token}` } : {}),
    },
  }).catch(() => undefined);
  if (response === undefined || !response.ok) throw new Error('Não foi possível carregar a foto.');
  return URL.createObjectURL(await response.blob());
}

/**
 * POST /maintenance-requests/{id}/photos — upload multipart de foto (ANTES|DEPOIS|comprovantes).
 * Multipart usa o httpClient compartilhado: CSRF em memória e replay único após refresh401.
 */
export async function uploadPhoto(id: string, photoType: MaintenancePhotoUploadType, fileName: string, file: File, operationId?: string): Promise<void> {
  const form = new FormData();
  form.append('photoType', photoType);
  if (operationId !== undefined) form.append('operationId', operationId);
  // Contrato multipart real (openapi/tecnico.v1.json): o ARQUIVO viaja no campo `photo`
  // (required: photoType + photo). `fileName` é metadado extra ignorado pelo binding — o backend
  // usa o nome do arquivo recebido.
  form.append('photo', file);
  form.append('fileName', fileName);
  await httpClient.post<unknown>(`/maintenance-requests/${id}/photos`, form);
}

// ── Corte 2.1 — fundação do fluxo de atendimento ───────────────────────────────────────────

/** GET /maintenance-requests/{id}/payment-methods — meios de pagamento da confirmação de valor. */
export async function fetchPaymentMethods(id: string): Promise<PaymentMethodOption[]> {
  const payload = await httpClient.get<unknown>(`/maintenance-requests/${id}/payment-methods`);
  return parsePaymentMethods(payload);
}

/** GET /equipment-categories/{categoryId}/step-suggestions — passos sugeridos por categoria. */
export async function fetchStepSuggestions(categoryId: number): Promise<{ suggestions: string[] }> {
  const payload = await httpClient.get<unknown>(`/equipment-categories/${String(categoryId)}/step-suggestions`);
  return parseStepSuggestions(payload);
}

export interface CategoryReviewInput {
  equipmentCategoryId: number;
  checklistItems: Array<{ description: string; displayOrder: number }>;
  technicianNotes?: string | null;
}

/** PUT /maintenance-requests/{id}/category-review — revisão de categoria + checklist inicial (204). */
export async function reviewCategory(id: string, input: CategoryReviewInput): Promise<void> {
  await httpClient.put<unknown>(`/maintenance-requests/${id}/category-review`, {
    equipmentCategoryId: input.equipmentCategoryId,
    checklistItems: input.checklistItems,
    technicianNotes: input.technicianNotes ?? null,
  });
}

/**
 * GET /equipment-categories — catálogo de categorias de equipamento. O backend libera anônimo
 * (lookup pré-login), mas o fluxo o consome LOGADO — httpClient normal, sem exceção.
 */
export async function fetchEquipmentCategories(): Promise<CategoryOption[]> {
  const payload = await httpClient.get<unknown>('/equipment-categories');
  return parseEquipmentCategories(payload);
}
